/**
 * Atlas PM · 用户选择器（P6.27，P8.5 支持多选）
 *
 * 统一的「从真实用户中选一个人 / 多个人」控件，供项目负责人 / 项目成员 /
 * 任务责任人复用。
 * 历史缺口：任务责任人是手填文本框（可填任意字符串），项目表单干脆没有负责人字段，
 * 导致审批第 1 级解析不到有效审批人。
 *
 * 数据源：GET /projects/owner-options（需 project:view）
 *   - 只返回启用用户，支持按姓名 / 用户名搜索；
 *   - 通过 includeId 回显原负责人（可能已停用）。
 *
 * ⚠️ 不能用 GET /system/users 作为数据源：它要求 user:manage，只有超管拥有，
 *    省管理员 / 项目经理将无法配置负责人。
 *
 * ⭐ P8.5：新增多选模式（`multiple` + `values` + `onChangeMulti`）。
 *   - 单选调用方完全不变（仍用 `value` + `onChange`）；
 *   - 多选时选中项以 chip 展示，支持退格删除最后一项、清除全部、键盘上下 + 回车。
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../api/client';
import './user-picker.css';

export interface UserOption {
  id: string;
  username: string;
  displayName: string;
  orgName: string | null;
  /** 1 启用 / 0 停用 */
  status: number;
}

interface UserPickerCommonProps {
  disabled?: boolean;
  /** 是否允许清除已选用户（编辑存量数据时有用） */
  allowClear?: boolean;
  placeholder?: string;
  /** 容器 data-testid；输入框 / 菜单 / 选项 / chip 会自动派生后缀 */
  dataTestId?: string;
  required?: boolean;
  /** 不出现在候选列表中的用户（例如已是负责人的用户） */
  excludeIds?: string[];
}

export interface UserPickerSingleProps extends UserPickerCommonProps {
  multiple?: false;
  /** 当前选中的用户 id（空串表示未选） */
  value: string;
  onChange(id: string, user?: UserOption): void;
  values?: never;
  onChangeMulti?: never;
}

export interface UserPickerMultiProps extends UserPickerCommonProps {
  multiple: true;
  /** 当前选中的用户 id 列表 */
  values: string[];
  onChangeMulti(ids: string[], users: UserOption[]): void;
  value?: never;
  onChange?: never;
}

export type UserPickerProps = UserPickerSingleProps | UserPickerMultiProps;

const EMPTY_IDS: string[] = [];

export default function UserPicker(props: UserPickerProps) {
  const {
    disabled = false,
    allowClear = true,
    placeholder = '搜索姓名 / 用户名',
    dataTestId,
    required = false,
    excludeIds,
  } = props;
  const multiple = props.multiple === true;
  const value = props.multiple ? '' : props.value;
  const values = props.multiple ? props.values : EMPTY_IDS;

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [debounced, setDebounced] = useState('');
  const [label, setLabel] = useState('');
  const [selected, setSelected] = useState<Record<string, UserOption>>({});
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(keyword.trim()), 200);
    return () => clearTimeout(timer);
  }, [keyword]);

  const query = useQuery({
    queryKey: multiple
      ? (['owner-options-multi', debounced] as const)
      : (['owner-options', debounced, value] as const),
    queryFn: () =>
      api.get<UserOption[]>(
        `/projects/owner-options${qs({
          keyword: debounced || undefined,
          includeId: multiple ? undefined : value || undefined,
        })}`,
      ),
    staleTime: 30_000,
  });

  const options = useMemo(() => query.data ?? [], [query.data]);

  // 多选：缓存已选用户信息，回显 chip 文案（编辑时由候选项补齐）
  useEffect(() => {
    if (!multiple) return;
    setSelected((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const o of options) {
        if (values.includes(o.id) && next[o.id] !== o) {
          next[o.id] = o;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [multiple, options, values]);

  useEffect(() => {
    if (!value) {
      setLabel('');
      return;
    }
    const hit = options.find((o) => o.id === value);
    if (hit) setLabel(hit.displayName);
    else setLabel((prev) => prev || `#${value}`);
  }, [value, options]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // 多选：过滤已选 / 显式排除的用户
  const candidates = useMemo(() => {
    if (!multiple) return options;
    const excluded = new Set([...values, ...(excludeIds ?? [])]);
    return options.filter((o) => !excluded.has(o.id));
  }, [multiple, options, values, excludeIds]);

  useEffect(() => {
    setActiveIndex(candidates.length > 0 ? 0 : -1);
  }, [candidates.length, debounced]);

  const emitSingle = (id: string, user?: UserOption) => {
    if (props.multiple) return;
    props.onChange(id, user);
  };

  const emitMulti = (ids: string[], users: UserOption[]) => {
    if (!props.multiple) return;
    props.onChangeMulti(ids, users);
  };

  const userOf = (id: string): UserOption | undefined =>
    selected[id] ?? options.find((o) => o.id === id);

  const userLabel = (id: string): string => userOf(id)?.displayName ?? `#${id}`;

  const select = (u: UserOption) => {
    if (multiple) {
      const ids = [...values, u.id];
      setSelected((prev) => ({ ...prev, [u.id]: u }));
      emitMulti(ids, ids.map((id) => (id === u.id ? u : userOf(id))).filter(Boolean) as UserOption[]);
      setKeyword('');
      setActiveIndex(0);
      // 多选保持展开，方便连续添加
      return;
    }
    setLabel(u.displayName);
    emitSingle(u.id, u);
    setOpen(false);
    setKeyword('');
  };

  const remove = (id: string) => {
    const ids = values.filter((v) => v !== id);
    emitMulti(ids, ids.map((v) => userOf(v)).filter(Boolean) as UserOption[]);
  };

  const clear = () => {
    if (multiple) {
      emitMulti([], []);
      setKeyword('');
      return;
    }
    emitSingle('');
    setLabel('');
    setKeyword('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'Backspace' && multiple && !keyword && values.length > 0) {
      e.preventDefault();
      remove(values[values.length - 1]!);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (candidates.length === 0) return;
      setActiveIndex((i) => {
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const base = i < 0 ? 0 : i + delta;
        return (base + candidates.length) % candidates.length;
      });
      return;
    }
    if (e.key === 'Enter' && open) {
      const hit = candidates[activeIndex] ?? (candidates.length === 1 ? candidates[0] : undefined);
      if (hit) {
        e.preventDefault();
        select(hit);
      }
    }
  };

  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);
  const hasValue = multiple ? values.length > 0 : Boolean(value);

  const input = (
    <input
      type="text"
      className={
        multiple
          ? 'user-picker__input user-picker__input--inline'
          : 'pm-input user-picker__input'
      }
      data-testid={dataTestId}
      role="combobox"
      aria-expanded={open}
      aria-required={required || undefined}
      autoComplete="off"
      disabled={disabled}
      placeholder={placeholder}
      value={multiple ? keyword : open ? keyword : label}
      onFocus={() => {
        if (!disabled) {
          setOpen(true);
          setKeyword('');
        }
      }}
      onChange={(e) => {
        setKeyword(e.target.value);
        setOpen(true);
      }}
      onKeyDown={onKeyDown}
    />
  );

  return (
    <div
      className={`user-picker${multiple ? ' user-picker--multi' : ''}`}
      ref={boxRef}
      data-testid={testId('picker')}
    >
      {multiple ? (
        <div className="pm-input user-picker__field" data-testid={testId('chips')}>
          {values.map((id) => (
            <span
              key={id}
              className="user-picker__chip"
              data-testid={testId(`chip-${id}`)}
              data-user-id={id}
            >
              <span className="user-picker__chip-name">{userLabel(id)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="user-picker__chip-x"
                  data-testid={testId(`chip-${id}-remove`)}
                  aria-label={`移除 ${userLabel(id)}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => remove(id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {input}
        </div>
      ) : (
        input
      )}
      {allowClear && hasValue && !disabled && (
        <button
          type="button"
          className="user-picker__clear"
          data-testid={testId('clear')}
          aria-label={multiple ? '清除已选成员' : '清除负责人'}
          onClick={clear}
        >
          ×
        </button>
      )}
      {open && (
        <div
          className="user-picker__menu"
          data-testid={testId('menu')}
          role="listbox"
          aria-label="用户候选"
          aria-multiselectable={multiple || undefined}
        >
          {query.isLoading && <div className="user-picker__empty">加载中…</div>}
          {query.isError && (
            <div className="user-picker__empty" data-testid={testId('error')}>
              用户列表加载失败
            </div>
          )}
          {!query.isLoading && !query.isError && candidates.length === 0 && (
            <div className="user-picker__empty" data-testid={testId('empty')}>
              无匹配的启用用户
            </div>
          )}
          {candidates.map((u, i) => {
            const isSelected = !multiple && u.id === value;
            return (
              <button
                type="button"
                key={u.id}
                role="option"
                aria-selected={isSelected}
                className={[
                  'user-picker__item',
                  isSelected ? 'is-selected' : '',
                  multiple && i === activeIndex ? 'is-active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-testid={testId(`option-${u.id}`)}
                data-user-id={u.id}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => multiple && setActiveIndex(i)}
                onClick={() => select(u)}
              >
                <span className="user-picker__name">{u.displayName}</span>
                <span className="user-picker__sub">
                  {u.username}
                  {u.orgName ? ` · ${u.orgName}` : ''}
                  {u.status !== 1 ? ' · 已停用' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
